import { useEffect, useState, useCallback } from "react";
import { Inbox, Send, FileEdit, Trash2, Archive, RotateCcw, Search, Mail, MailOpen, Reply, ReplyAll, Forward, Paperclip, Download, X, Loader2, AlertTriangle, User, Phone, FileText } from "lucide-react";
import { api, apiError, fmtDateTime, euro } from "@/lib/api";
import { toast } from "sonner";

const FOLDERS = [
  ["inbox", Inbox, "Posteingang"], ["sent", Send, "Gesendet"], ["drafts", FileEdit, "Entwürfe"],
  ["archive", Archive, "Archiv"], ["trash", Trash2, "Papierkorb"],
];

const payDe = (s) => ({ paid: "Bezahlt", payment_requested: "Aufforderung offen", pending: "Ausstehend", refunded: "Erstattet", free: "Kostenlos" }[s] || s || "—");

const Mailbox = () => {
  const [summary, setSummary] = useState(null);
  const [folder, setFolder] = useState("inbox");
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [compose, setCompose] = useState(null); // {to, cc, subject, body, reply_to_id, attachments}

  const loadSummary = useCallback(() => api.get("/admin/mailbox/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  const loadList = useCallback(() => api.get(`/admin/mailbox/messages?folder=${folder}&q=${encodeURIComponent(q)}`).then((r) => setList(r.data)).catch(() => {}), [folder, q]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { const iv = setInterval(() => { loadSummary(); loadList(); }, 60000); return () => clearInterval(iv); }, [loadSummary, loadList]);

  const syncNow = async () => {
    setBusy(true);
    try { await api.post("/admin/mailbox/sync"); await loadSummary(); await loadList(); toast.success("Postfach synchronisiert"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const open = async (m) => {
    try {
      const { data } = await api.get(`/admin/mailbox/messages/${m.id}`);
      setDetail(data);
      if (m.unread) { await api.post(`/admin/mailbox/messages/${m.id}/read`, { read: true }); loadList(); loadSummary(); }
    } catch (e) { toast.error(apiError(e)); }
  };

  const markUnread = async (m) => { await api.post(`/admin/mailbox/messages/${m.id}/read`, { read: false }); setDetail(null); loadList(); loadSummary(); };
  const move = async (m, target) => {
    await api.post(`/admin/mailbox/messages/${m.id}/move`, { target });
    toast.success(target === "trash" ? "In den Papierkorb verschoben" : target === "archive" ? "Archiviert" : "Verschoben");
    setDetail(null); loadList(); loadSummary();
  };

  const startCompose = (mode) => {
    const m = detail?.message;
    if (mode === "new" || !m) { setCompose({ to: "", cc: "", subject: "", body: "", reply_to_id: "", attachments: [] }); return; }
    const quote = `\n\n----- Ursprüngliche Nachricht -----\nVon: ${m.from_name || m.from_addr}\nDatum: ${fmtDateTime(m.date)}\nBetreff: ${m.subject}\n\n${(m.text || "").slice(0, 3000)}`;
    if (mode === "reply") setCompose({ to: m.from_addr, cc: "", subject: `Re: ${m.subject}`, body: quote, reply_to_id: m.id, attachments: [] });
    if (mode === "replyAll") setCompose({ to: m.from_addr, cc: m.cc || "", subject: `Re: ${m.subject}`, body: quote, reply_to_id: m.id, attachments: [] });
    if (mode === "forward") setCompose({ to: "", cc: "", subject: `Fwd: ${m.subject}`, body: quote, reply_to_id: "", attachments: [] });
  };

  const addFiles = (files) => {
    [...files].forEach((f) => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: max. 10 MB`); return; }
      const rd = new FileReader();
      rd.onload = () => setCompose((c) => ({ ...c, attachments: [...c.attachments, { filename: f.name, content_b64: String(rd.result).split(",")[1] }] }));
      rd.readAsDataURL(f);
    });
  };

  const sendMail = async () => {
    if (!compose.to || !compose.subject) { toast.error("Empfänger und Betreff angeben"); return; }
    setBusy(true);
    try { await api.post("/admin/mailbox/send", compose); toast.success("E-Mail gesendet"); setCompose(null); loadSummary(); if (folder === "sent") loadList(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const dl = async (att) => {
    const res = await api.get(`/admin/mailbox/attachments/${att.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = att.filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  const m = detail?.message;
  return (
    <div data-testid="admin-mailbox">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="font-heading text-4xl uppercase tracking-tight text-white">E-Mail-Postfach</h1>
        <div className="flex gap-2">
          <button onClick={syncNow} disabled={busy} data-testid="mailbox-sync-btn" className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:text-[#ff0033] px-3 py-2 font-mono-lab text-xs uppercase tracking-widest disabled:opacity-50"><RotateCcw size={14} className={busy ? "animate-spin" : ""} /> Aktualisieren</button>
          <button onClick={() => startCompose("new")} data-testid="mailbox-compose-btn" className="bg-[#ff0033] text-white font-mono-lab text-xs uppercase tracking-widest px-4 py-2">Neue E-Mail</button>
        </div>
      </div>
      {summary && !summary.configured && (
        <div className="mb-4 border border-amber-400/40 bg-amber-400/5 p-3 text-amber-400 text-sm">IMAP nicht konfiguriert – bitte Zugangsdaten in den Umgebungsvariablen prüfen.</div>
      )}
      {summary?.priority_unread > 0 && (
        <div className="mb-4 border border-[#ff0033]/40 bg-[#ff0033]/10 p-3 text-sm text-white flex items-center gap-2" data-testid="mailbox-priority-hint">
          <AlertTriangle size={15} className="text-[#ff0033]" /> {summary.priority_unread} ungelesene Antwort(en) auf Buchungs-/Zahlungsmails – bitte prüfen.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[190px_360px_1fr] gap-4 min-h-[60vh]">
        {/* Ordner */}
        <div className="bg-[#0F0F0F] border border-white/10 p-2 flex lg:flex-col gap-1 overflow-x-auto">
          {FOLDERS.map(([key, Icon, label]) => {
            const f = summary?.folders?.find((x) => x.key === key);
            return (
              <button key={key} onClick={() => { setFolder(key); setDetail(null); }} data-testid={`mailbox-folder-${key}`}
                className={`flex items-center gap-2.5 px-3 py-2.5 font-mono-lab text-[11px] uppercase tracking-widest whitespace-nowrap ${folder === key ? "bg-[#ff0033] text-white" : "text-neutral-400 hover:text-white"}`}>
                <Icon size={15} /> <span>{label}</span>
                {f?.unread > 0 && <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full ${folder === key ? "bg-black/30" : "bg-[#ff0033] text-white"}`}>{f.unread}</span>}
              </button>
            );
          })}
        </div>

        {/* Liste */}
        <div className="bg-[#0F0F0F] border border-white/10 flex flex-col max-h-[75vh]">
          <div className="p-2 border-b border-white/10 flex items-center gap-2">
            <Search size={14} className="text-neutral-500 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen (Absender, Betreff, Inhalt)…" data-testid="mailbox-search"
              className="w-full bg-transparent text-white text-sm outline-none placeholder:text-neutral-600" />
          </div>
          <div className="overflow-y-auto flex-1">
            {list.length === 0 && <p className="p-5 text-neutral-500 text-sm">Keine Nachrichten.</p>}
            {list.map((it) => (
              <button key={it.id} onClick={() => open(it)} data-testid={`mailbox-msg-${it.id}`}
                className={`w-full text-left px-3 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${m?.id === it.id ? "bg-white/5" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm truncate ${it.unread ? "text-white font-semibold" : "text-neutral-400"}`}>
                    {it.unread && <span className="inline-block w-2 h-2 rounded-full bg-[#ff0033] mr-2" />}
                    {folder === "sent" ? (it.to || "—") : (it.from_name || it.from_addr)}
                  </span>
                  <span className="text-neutral-600 text-[10px] shrink-0">{it.date ? fmtDateTime(it.date) : ""}</span>
                </div>
                <p className={`text-xs truncate mt-0.5 ${it.unread ? "text-neutral-200" : "text-neutral-500"}`}>{it.subject || "(kein Betreff)"}</p>
                <p className="text-neutral-600 text-[11px] truncate mt-0.5">{it.snippet}</p>
                <div className="flex gap-1.5 mt-1">
                  {it.cancel_flag && <span className="text-[9px] font-mono-lab uppercase tracking-widest text-amber-400 border border-amber-400/40 px-1.5 py-0.5">Mögliche Stornierungsanfrage</span>}
                  {it.system_reply && <span className="text-[9px] font-mono-lab uppercase tracking-widest text-sky-400 border border-sky-400/40 px-1.5 py-0.5">Antwort auf System-Mail</span>}
                  {it.attachments?.length > 0 && <Paperclip size={11} className="text-neutral-500" />}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Nachricht */}
        <div className="bg-[#0F0F0F] border border-white/10 max-h-[75vh] overflow-y-auto">
          {!m ? (
            <div className="h-full flex items-center justify-center text-neutral-600 text-sm p-10"><Mail size={18} className="mr-2" /> Nachricht auswählen</div>
          ) : (
            <div className="p-4 md:p-5" data-testid="mailbox-detail">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-heading text-xl md:text-2xl uppercase tracking-tight text-white leading-tight">{m.subject || "(kein Betreff)"}</h2>
                  <p className="text-neutral-400 text-xs mt-1">Von: <span className="text-neutral-200">{m.from_name || m.from_addr}</span> &lt;{m.from_addr}&gt; · {fmtDateTime(m.date)}</p>
                  <p className="text-neutral-500 text-xs">An: {m.to}{m.cc ? ` · Cc: ${m.cc}` : ""}</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => startCompose("reply")} data-testid="mail-reply" title="Antworten" className="p-2 border border-white/15 text-neutral-300 hover:text-[#ff0033]"><Reply size={15} /></button>
                  <button onClick={() => startCompose("replyAll")} data-testid="mail-reply-all" title="Allen antworten" className="p-2 border border-white/15 text-neutral-300 hover:text-[#ff0033]"><ReplyAll size={15} /></button>
                  <button onClick={() => startCompose("forward")} data-testid="mail-forward" title="Weiterleiten" className="p-2 border border-white/15 text-neutral-300 hover:text-[#ff0033]"><Forward size={15} /></button>
                  <button onClick={() => markUnread(m)} title="Als ungelesen markieren" className="p-2 border border-white/15 text-neutral-300 hover:text-[#ff0033]"><MailOpen size={15} /></button>
                  {m.folder !== "archive" && <button onClick={() => move(m, "archive")} data-testid="mail-archive" title="Archivieren" className="p-2 border border-white/15 text-neutral-300 hover:text-[#ff0033]"><Archive size={15} /></button>}
                  {m.folder !== "trash" ? (
                    <button onClick={() => move(m, "trash")} data-testid="mail-trash" title="Löschen" className="p-2 border border-[#ff0033]/40 text-[#ff0033] hover:bg-[#ff0033]/10"><Trash2 size={15} /></button>
                  ) : (
                    <button onClick={() => move(m, "inbox")} title="Wiederherstellen" className="p-2 border border-white/15 text-neutral-300 hover:text-white"><Inbox size={15} /></button>
                  )}
                </div>
              </div>

              {detail.customer && (
                <div className="mt-4 border border-sky-400/30 bg-sky-400/5 p-3 text-sm" data-testid="mail-customer-box">
                  <p className="font-mono-lab text-[10px] uppercase tracking-widest text-sky-400 mb-1.5">Zugeordneter Kunde</p>
                  <p className="text-white flex items-center gap-2"><User size={13} className="text-sky-400" /> {detail.customer.name || detail.customer.email}{detail.customer.customer_id && <span className="text-neutral-500 text-xs">· {detail.customer.customer_id.slice(0, 8)}</span>}</p>
                  {detail.customer.phone && <p className="text-neutral-300 text-xs mt-1 flex items-center gap-2"><Phone size={12} className="text-sky-400" /> {detail.customer.phone}</p>}
                  {detail.bookings?.slice(0, 3).map((b) => (
                    <p key={b.booking_number} className="text-neutral-300 text-xs mt-1">📅 {b.booking_number} · {b.course_name} · {fmtDateTime(b.start)} · {euro(b.amount)} · <span className={b.payment_status === "paid" ? "text-green-400" : "text-amber-400"}>{payDe(b.payment_status)}</span></p>
                  ))}
                  {detail.invoices?.slice(0, 2).map((inv) => (
                    <p key={inv.invoice_number} className="text-neutral-400 text-xs mt-1 flex items-center gap-1.5"><FileText size={11} /> Rechnung {inv.invoice_number} · {inv.status}</p>
                  ))}
                </div>
              )}

              {m.attachments?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {m.attachments.map((a) => (
                    <button key={a.id} onClick={() => dl(a)} data-testid={`mail-att-${a.id}`} className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:text-[#ff0033] px-3 py-2 text-xs">
                      <Paperclip size={12} /> {a.filename} <Download size={12} />
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-4 border-t border-white/10 pt-4">
                {m.html ? (
                  <iframe title="mail" sandbox="" srcDoc={`<base target="_blank"><style>body{font-family:Arial,sans-serif;color:#e5e5e5;background:#0F0F0F;font-size:14px;line-height:1.5;margin:0}a{color:#ff5577}</style>${m.html}`}
                    className="w-full min-h-[320px] border-0" onLoad={(e) => { try { e.target.style.height = e.target.contentDocument.body.scrollHeight + 40 + "px"; } catch { /* sandboxed */ } }} />
                ) : (
                  <pre className="text-neutral-200 text-sm whitespace-pre-wrap font-sans">{m.text || "(kein Inhalt)"}</pre>
                )}
              </div>

              {detail.thread?.length > 0 && (
                <div className="mt-6 border-t border-white/10 pt-4">
                  <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Unterhaltung ({detail.thread.length})</p>
                  {detail.thread.map((tmsg) => (
                    <div key={tmsg.id} className="border border-white/10 p-3 mb-2">
                      <p className="text-neutral-400 text-xs">{tmsg.folder === "sent" ? "Du" : (tmsg.from_name || tmsg.from_addr)} · {fmtDateTime(tmsg.date)}</p>
                      <p className="text-neutral-300 text-xs mt-1 whitespace-pre-wrap">{(tmsg.text || tmsg.snippet || "").slice(0, 600)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Compose */}
      {compose && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="mail-compose-modal">
          <div className="bg-[#0F0F0F] border border-white/15 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl uppercase tracking-tight text-white">{compose.reply_to_id ? "Antworten" : compose.subject.startsWith("Fwd:") ? "Weiterleiten" : "Neue E-Mail"}</h3>
              <button onClick={() => setCompose(null)} className="text-neutral-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-2">
              <input value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} placeholder="An" data-testid="compose-to" className="w-full bg-[#1A1A1A] border border-white/10 text-white text-sm p-2.5 outline-none focus:border-[#ff0033]" />
              <input value={compose.cc} onChange={(e) => setCompose({ ...compose, cc: e.target.value })} placeholder="Cc (optional)" data-testid="compose-cc" className="w-full bg-[#1A1A1A] border border-white/10 text-white text-sm p-2.5 outline-none focus:border-[#ff0033]" />
              <input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} placeholder="Betreff" data-testid="compose-subject" className="w-full bg-[#1A1A1A] border border-white/10 text-white text-sm p-2.5 outline-none focus:border-[#ff0033]" />
              <textarea value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} rows={10} placeholder="Nachricht…" data-testid="compose-body" className="w-full bg-[#1A1A1A] border border-white/10 text-white text-sm p-2.5 outline-none focus:border-[#ff0033]" />
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:text-white px-3 py-2 text-xs cursor-pointer">
                  <Paperclip size={13} /> Anhang hinzufügen
                  <input type="file" multiple className="hidden" data-testid="compose-attach" onChange={(e) => addFiles(e.target.files)} />
                </label>
                {compose.attachments.map((a, i) => (
                  <span key={i} className="flex items-center gap-1.5 bg-[#1A1A1A] border border-white/10 text-neutral-300 px-2 py-1 text-xs">
                    {a.filename} <button onClick={() => setCompose({ ...compose, attachments: compose.attachments.filter((_, j) => j !== i) })} className="text-neutral-500 hover:text-[#ff0033]"><X size={11} /></button>
                  </span>
                ))}
              </div>
              <button onClick={sendMail} disabled={busy} data-testid="compose-send" className="bg-[#ff0033] text-white font-heading uppercase tracking-wider px-6 py-3 text-sm hover:bg-[#cc0029] disabled:opacity-50 flex items-center gap-2">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Senden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mailbox;
